/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import Chart from "react-apexcharts";
import CountInfo from "../../components/Composables/TilesCountInfo";
import API from "../../Utils/Interceptor/ApiMiddleware";
import LoadingSpinner from "../../components/Composables/CircularProgressBar";
import dayjs from "dayjs";

const EmployerDashboard = () => {
  const initialFilters = {
    Candidates: "M",
    Users: "M",
    Jobs: "M",
  };

  const [countData, setCountData] = useState([]);
  const [activeFilters, setActiveFilters] = useState(initialFilters);
  const [chartData, setChartData] = useState({
    Candidates: { categories: [], data: [], text: "" },
    Users: { categories: [], data: [], text: "" },
    Jobs: { categories: [], data: [], text: "" },
  });
  const [loadingStatus, setLoadingStatus] = useState({
    count: false,
    candidateChart: false,
    userChart: false,
    jobChart: false,
  });
  const [payload, setPayload] = useState({
    graph: "all",
    filters: "M",
  });

  const chartColors = {
    Candidates: "#27BB2E",
    Users: "#3775AF",
    Jobs: "#F78426",
  };

  const updateLoading = (key, value) => {
    setLoadingStatus((prev) => ({ ...prev, [key]: value }));
  };

  const parseData = (data = [], filter) => {
    const categories = data.map((item) => {
      const { period } = item;
      switch (filter) {
        case "M":
          return dayjs(period, "YYYY-MM").format("MMM-YY");
        case "Q":
          const [quarter, year] = period.split("(");
          return `${quarter}(${year.slice(-3, -1)})`;
        case "Y":
          return period;
        default:
          return period;
      }
    });

    const text =
      filter === "Q" ? "Quarters" : filter === "Y" ? "Years" : "Months";

    return {
      categories,
      data: data.map((item) => item.count),
      text,
    };
  };

  const fetchChartData = async () => {
    const chartPayload = {
      graph: payload.graph,
      filter: payload.filters.toLowerCase(),
    };

    try {
      if (chartPayload.graph === "all") {
        updateLoading("userChart", true);
        updateLoading("candidateChart", true);
        updateLoading("jobChart", true);
      } else {
        updateLoading(`${chartPayload.graph}Chart`, true);
      }

      const response = await API.post("/dashboard/graph-data", chartPayload, {
        withCredentials: true,
      });

      if (response?.data?.success) {
        const { Candidates, Users, Jobs } = response.data.data;
        if (chartPayload.graph === "all") {
          updateLoading("userChart", false);
          updateLoading("candidateChart", false);
          updateLoading("jobChart", false);
        }

        setChartData((prev) => {
          const chartDataMap = {
            Users: parseData(Users, payload.filters),
            Candidates: parseData(Candidates, payload.filters),
            Jobs: parseData(Jobs, payload.filters),
          };

          return chartPayload.graph in chartDataMap
            ? {
                ...prev,
                [chartPayload.graph]: chartDataMap[chartPayload.graph],
              }
            : chartDataMap;
        });
      }
    } catch (error) {
      console.error("Error fetching chart data:", error.message);
    } finally {
      if (chartPayload.graph === "all") {
        updateLoading("userChart", false);
        updateLoading("candidateChart", false);
        updateLoading("jobChart", false);
      } else updateLoading(`${chartPayload.graph}Chart`, false);
    }
  };

  const handleFilterChange = (boxName, filter) => {
    setActiveFilters((prev) => ({ ...prev, [boxName]: filter }));
    setPayload({
      graph: boxName,
      filters: filter,
    });
  };

  const fetchCountData = async () => {
    try {
      const payload = {
        tile_type: "employer_dashboard",
      };
      updateLoading("count", true);

      const response = await API.post("/tiles/get-count", payload);
      const rawData = response.data.data;

      const transformedData = rawData.reduce((acc, item) => {
        const existingItem = acc.find((entry) => entry.title === item.title);
        if (existingItem) {
          existingItem.data = [...existingItem.data, ...item.data];
        } else {
          acc.push(item);
        }
        return acc;
      }, []);
      setCountData(transformedData);
    } catch (error) {
      console.error("Error fetching count data:", error.message);
    } finally {
      updateLoading("count", false);
    }
  };

  useEffect(() => {
    fetchCountData();
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [payload]);

  const generateChartOptions = (categories, text, color, title) => ({
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: { columnWidth: "40%", distributed: true },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    colors: [color],
    xaxis: {
      categories,
      title: {
        text,
        style: { fontSize: "14px", fontWeight: "600", color: "#333" },
      },
    },
    yaxis: {
      title: {
        text: "No. of " + title,
        style: { fontSize: "14px", fontWeight: "600", color: "#333" },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => val,
      },
    },
  });

  return (
    <Box
      sx={{
        background: "#E3F2FD",
        padding: "16px 16px 8px",
        height: "100%",
        borderRadius: "24px 0 0 0",
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "12px 24px",
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: "600" }}>
          Dashboard
        </Typography>
      </Box>

      {loadingStatus.count ? (
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            flex: 1,
            textAlign: "center",
            padding: "20px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "150px",
            marginTop: "20px",
          }}
        >
          <LoadingSpinner />
        </Box>
      ) : countData.length > 0 ? (
        <CountInfo sections={countData} showHeader={true} />
      ) : (
        <CountInfo sections={[]} showHeader={true} />
      )}

      {/* <Box
        sx={{
          marginTop: "20px",
          display: "flex",
          gap: "20px",
          justifyContent: "space-between",
        }}
      >
        {["candidate", "user", "job"].map((boxName) => (
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "100%",
              minHeight: "300px",
            }}
            key={boxName}
          >
            <Box
              sx={{
                backgroundColor: "#F7F7F7",
                padding: "12px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderRadius: "8px 8px 0 0",
              }}
            >
              <Typography sx={{ fontSize: "18px", fontWeight: "600" }}>
                {boxName.charAt(0).toUpperCase() + boxName.slice(1) + "s"}
              </Typography>
              <Box>
                {["M", "Q", "Y"].map((filter) => (
                  <Button
                    key={filter}
                    onClick={() => handleFilterChange(boxName, filter)}
                    sx={{
                      backgroundColor:
                        activeFilters[boxName] === filter
                          ? "#3F95EA"
                          : "#68B0DA4D",
                      color:
                        activeFilters[boxName] === filter ? "white" : "black",
                      fontSize: "12px",
                      minWidth: "30px",
                      fontWeight: "700",
                      marginRight: "8px",
                      borderRadius: "4px",
                    }}
                  >
                    {filter}
                  </Button>
                ))}
              </Box>
            </Box>

            <Box
              sx={{ padding: "24px 24px 0", height: 290, position: "relative" }}
            >
              {loadingStatus[`${boxName}Chart`] ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <LoadingSpinner />
                </Box>
              ) : (
                <Chart
                  options={generateChartOptions(
                    chartData[boxName].categories,
                    chartData[boxName].text,
                    chartColors[boxName]
                  )}
                  series={[{ name: boxName, data: chartData[boxName].data }]}
                  type="bar"
                  height={250}
                />
              )}
            </Box>
          </Box>
        ))}
      </Box>
       */}

      {/* Added for responsiceness  */}
      <Box
        sx={{
          marginTop: "20px",
          display: "flex",
          flexWrap: "wrap",
          gap: "20px",
          justifyContent: "space-between",
        }}
      >
        {["Candidates", "Users", "Jobs"].map((boxName) => (
          <Box
            key={boxName}
            sx={{
              backgroundColor: "white",
              borderRadius: "12px",
              flex: "1 1 calc(33.333% - 20px)", // Adjust for three boxes per row
              minWidth: "280px", // Ensures boxes don't shrink too much
              maxWidth: "100%",
              minHeight: "300px",
            }}
          >
            <Box
              sx={{
                backgroundColor: "#F9F9F9;",
                padding: "0px 24px",
                display: "flex",
                height: "44px",
                justifyContent: "space-between",
                alignItems: "center",
                alignSelf: "stretch",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <Typography sx={{ fontSize: "18px", fontWeight: "600" }}>
                {boxName.charAt(0).toUpperCase() + boxName.slice(1)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {["M", "Q", "Y"].map((filter) => (
                  <Button
                    key={filter}
                    onClick={() => handleFilterChange(boxName, filter)}
                    sx={{
                      backgroundColor:
                        activeFilters[boxName] === filter
                          ? "#3F95EA"
                          : "#68B0DA4D",
                      color:
                        activeFilters[boxName] === filter ? "white" : "black",
                      fontSize: "12px",
                      fontWeight:
                        activeFilters[boxName] === filter ? "700" : "400",
                      display: "flex",
                      width: "24px",
                      height: "24px",
                      padding: "4px 8px",
                      justifyContent: "center",
                      alignItems: "center",
                      minWidth: 0,
                    }}
                  >
                    {filter}
                  </Button>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                padding: "24px 24px 18px",
                flexGrow: 1,
                minHeight: 0,
                height: "calc(100vh - 410px)",
                position: "relative",
              }}
            >
              {loadingStatus[`${boxName}Chart`] ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <LoadingSpinner />
                </Box>
              ) : (
                <Chart
                  options={generateChartOptions(
                    chartData[boxName].categories,
                    chartData[boxName].text,
                    chartColors[boxName],
                    boxName
                  )}
                  series={[{ name: boxName, data: chartData[boxName].data }]}
                  type="bar"
                  height="100%"
                />
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default EmployerDashboard;
